import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiZap, FiMail, FiLock } = FiIcons;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Simulate login process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock user data - in real app this would come from API
      const userData = {
        userId: `user-${Date.now()}`,
        token: `token-${Date.now()}`,
        newUser: !localStorage.getItem('hasLoggedInBefore'),
        email: formData.email
      };

      localStorage.setItem('hasLoggedInBefore', 'true');
      const isNewUser = login(userData);
      
      if (isNewUser) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-azure-600 to-azure-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <img
              src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
              alt="K³ Logo"
              className="h-24 w-auto mx-auto mb-8 filter brightness-0 invert"
            />
            <h1 className="text-4xl font-bold mb-6">Willkommen zurück</h1>
            <p className="text-xl text-azure-100 mb-12 max-w-md leading-relaxed">
              Melde dich an und entdecke die Kraft der KI-Automatisierung für dein Unternehmen
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiShield} className="text-2xl mr-4" />
                <span>Sichere Anmeldung</span>
              </div>
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiUsers} className="text-2xl mr-4" />
                <span>Personalisierte Beratung</span>
              </div>
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiZap} className="text-2xl mr-4" />
                <span>KI-Lösungen für KMU</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
              alt="K³ Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-black">Anmeldung</h1>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-2">Anmelden</h2>
              <p className="text-gray-600">
                Zugang zu deiner KI-Beratungsplattform
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    placeholder="ihre.email@beispiel.de"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Passwort
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    placeholder="Ihr Passwort"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-azure-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-azure-700 focus:ring-2 focus:ring-azure-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Noch kein Konto?{' '}
                <span className="text-azure-600 font-semibold">
                  Die Registrierung erfolgt beim ersten Login
                </span>
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Probleme beim Anmelden?{' '}
              <a
                href="mailto:mike.trepte@ki-kundenkommunikation.de"
                className="text-azure-600 hover:text-azure-700 font-medium"
              >
                Kontaktiere uns
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;